import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Breadcrumb } from '../shared/components/breadcrumbs/breadcrumbs.component';
import { buildMenu, NavEntry, NavLink } from './nav-menu';
import { ShellService } from './shell.service';
import { FeatureService } from '../services/feature.service';

/**
 * Builds a page's breadcrumb trail from the sidebar menu, so trails follow the same hierarchy
 * the user navigates by: Dashboard › group › section › page.
 *
 * PERMISSIONS: the menu is built with the same role rules as the sidebar (nav-menu.ts), so a
 * step can only link to a page that is in this user's own menu. Groups and sections without a
 * page of their own (Reports, Settings, Accounting, ...) are plain text; a group or section
 * that names a `landing` page (Clients → List Clients) links there only if that page is in the
 * user's menu. A page that isn't in the user's menu at all gets no parent steps.
 */
@Injectable({
    providedIn: 'root'
})
export class BreadcrumbTrailService {
    constructor(private shell: ShellService, private features: FeatureService) { }

    /**
     * @param path  the menu route this page is, or hangs under (e.g. '/codes-list')
     * @param tail  steps below that page, e.g. a record name; when given, the page at `path`
     *              becomes a link back to itself
     */
    trail(path: string, tail: Breadcrumb[] = []): Observable<Breadcrumb[]> {
        // Settings only change the "Create New Inspection" route, so render straight away and
        // refine once they load.
        // Rebuilt when feature flags or the staff user's permissions load, so a trail never links to
        // a page the tenant or user can't open.
        return combineLatest(
            this.shell.settings$.pipe(map(settings => settings.inspection_tab_order), startWith(undefined)),
            this.shell.permissions$.pipe(startWith([] as string[])),
            this.features.changes$.pipe(startWith(null))
        ).pipe(map(([tabOrder, permissions]) => this.build(this.menu(tabOrder, permissions), path, tail)));
    }

    private menu(inspectionTabOrder: any, permissions: string[]): NavEntry[] {
        const user = this.shell.user;
        return buildMenu({
            isInspector: user.user_level == 'inspector',
            isAdministrator: user.user_level == 'administrator',
            inspectionTabOrder,
            apiSite: environment.apiSite,
            oldSite: environment.oldSite,
            permissions,
            hasFeature: key => this.features.has(key)
        });
    }

    // `path` is a route ('/codes-list') or '#<link id>' for a menu link that lives in twin-ci
    // ('#users-list').
    private build(menu: NavEntry[], path: string, tail: Breadcrumb[]): Breadcrumb[] {
        const clean = (path || '').split('?')[0];
        const isPage = (link: { id: string, route?: string }) =>
            clean.charAt(0) === '#' ? link.id === clean.slice(1) : link.route === clean;
        let found: { label: string, target: NavLink }[] = [];

        for (const entry of menu) {
            if (!entry.sections) {
                if (isPage(entry)) {
                    found = [{ label: entry.label, target: entry }];
                    break;
                }
                continue;
            }
            // A group that is also a page (Reports → Report Library).
            if (entry.route && isPage(entry)) {
                found = [{ label: entry.label, target: entry }];
                break;
            }
            const links = ([] as NavLink[]).concat(...entry.sections.map(s => s.links));
            const find = (id: string) => links.filter(l => l.id === id)[0];
            const section = entry.sections.filter(s => s.links.some(isPage))[0];
            if (!section) {
                continue;
            }
            const page = section.links.filter(isPage)[0];
            found.push({ label: entry.label, target: find(entry.landing) || (entry.route ? entry : undefined) });
            // A section named like its group (Clients › Clients) adds nothing.
            if (entry.sections.length > 1 && section.title && section.title !== entry.label) {
                found.push({ label: section.title, target: find(section.landing) });
            }
            // A group/section whose own page this is already names it: Clients › Agencies, not
            // Clients › Agencies › List Agencies.
            if (found[found.length - 1].target !== page) {
                found.push({ label: page.label, target: page });
            }
            break;
        }

        // Every step links to its page (only ever a page in this user's menu) except the last,
        // which is the page being viewed unless a tail follows it.
        const steps: Breadcrumb[] = found.map((step, i) => {
            const isLast = i === found.length - 1 && !tail.length;
            if (!step.target || isLast) {
                return { label: step.label };
            }
            return step.target.route ? { label: step.label, link: step.target.route } : { label: step.label, href: step.target.href };
        });
        return steps.concat(tail);
    }
}
